import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Calendar, User, Eye, Heart, MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import TreeLogo from '@/components/TreeLogo';
import TreeLoadingSpinner from '@/components/TreeLoadingSpinner';

// Get current season based on month
const getCurrentSeason = () => {
  const month = new Date().getMonth();
  if (month >= 2 && month <= 4) return 'spring';
  if (month >= 5 && month <= 7) return 'summer';
  if (month >= 8 && month <= 10) return 'autumn';
  return 'winter';
};

// Animated Seasonal Growing Branch Component
const SeasonalGrowingBranch = ({ className, style, delay = 0, direction = 'left', scale = 1, angle = 0 }) => {
  const isLeft = direction === 'left';
  const currentSeason = getCurrentSeason();
  
  // Calculate rotation angle - branches grow diagonally
  const rotationAngle = isLeft ? (angle || -25) : (angle || 25);
  
  const seasonColors = {
    spring: { trunk: 'hsl(30 35% 35%)', branch: 'hsl(30 35% 40%)', leaf: 'hsl(340 70% 75%)', leafAlt: 'hsl(100 40% 55%)' },
    summer: { trunk: 'hsl(30 40% 32%)', branch: 'hsl(30 40% 38%)', leaf: 'hsl(120 45% 40%)', leafAlt: 'hsl(100 50% 45%)' },
    autumn: { trunk: 'hsl(25 45% 30%)', branch: 'hsl(25 45% 35%)', leaf: 'hsl(25 80% 50%)', leafAlt: 'hsl(0 70% 55%)' },
    winter: { trunk: 'hsl(220 15% 35%)', branch: 'hsl(220 15% 40%)', leaf: 'hsl(200 30% 85%)', leafAlt: 'hsl(210 25% 90%)' }
  };
  
  const colors = seasonColors[currentSeason];
  
  return (
    <svg 
      className={`absolute pointer-events-none ${className}`}
      style={{ 
        ...style, 
        transform: `scale(${scale}) ${isLeft ? '' : 'scaleX(-1)'} rotate(${rotationAngle}deg)`,
        transformOrigin: isLeft ? 'bottom left' : 'bottom right'
      }}
      width="250" height="350" viewBox="0 0 250 350"
    >
      <path d="M30 350 Q25 280 40 220 Q55 160 35 110 Q15 60 45 20"
        stroke={colors.trunk} strokeWidth="5" fill="none" strokeLinecap="round"
        style={{ strokeDasharray: 500, strokeDashoffset: 500, animation: `growBranch 5s ease-out ${delay}s forwards` }}
      />
      <path d="M40 220 Q70 195 95 215 Q120 235 140 210"
        stroke={colors.branch} strokeWidth="3" fill="none" strokeLinecap="round"
        style={{ strokeDasharray: 180, strokeDashoffset: 180, animation: `growBranch 3s ease-out ${delay + 1.2}s forwards` }}
      />
      <circle cx="140" cy="210" r="4" fill={colors.leaf}
        style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 3.5}s forwards` }}
      />
      <circle cx="95" cy="215" r="3" fill={colors.leafAlt}
        style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4}s forwards` }}
      />
    </svg>
  );
};

const BlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchPosts();
    fetchTags();
  }, [selectedTag, currentPage]);

  const fetchPosts = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedTag) params.append('tag', selectedTag);
      params.append('page', currentPage);
      params.append('limit', 9);
      
      const response = await axios.get(`${API}/api/blog?${params}`);
      setPosts(response.data.posts);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/api/blog/tags`);
      setTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <TreeLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: 'hsl(45 25% 97%)' }}>
      {/* Animated branches */}
      <SeasonalGrowingBranch 
        direction="left" delay={0.3} scale={1}
        className="opacity-[0.08] fixed"
        style={{ left: '-50px', bottom: '-60px', zIndex: 0 }}
      />
      <SeasonalGrowingBranch 
        direction="right" delay={0.8} scale={1}
        className="opacity-[0.08] fixed"
        style={{ right: '-50px', bottom: '-60px', zIndex: 0 }}
      />

      {/* Navigation */}
      <nav className="sticky top-0 z-50" style={{ backgroundColor: 'hsl(220 15% 55%)' }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'hsl(15 40% 82%)' }}>
              <TreeLogo size={24} className="text-slate-600" />
            </div>
            <div>
              <h1 className="text-lg text-white font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                Rooted Blog
              </h1>
            </div>
          </Link>
          
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-white/80 hover:text-white"
              onClick={() => navigate('/')}
            >
              Home
            </Button>
            <Button 
              variant="ghost" 
              className="text-white/80 hover:text-white"
              onClick={() => navigate('/subscribe')}
            >
              Subscribe
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 px-6 text-center relative">
        <h1 className="text-4xl md:text-5xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(220 15% 30%)' }}>
          Faith & Reflection
        </h1>
        <p className="text-lg max-w-2xl mx-auto" style={{ color: 'hsl(220 10% 45%)' }}>
          Explore articles on faith, devotion, and spiritual growth from our community of writers.
        </p>
        
        {/* Search */}
        <div className="max-w-md mx-auto mt-8 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Search articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-full"
            data-testid="blog-search"
          />
        </div>
      </section>

      {/* Tags */}
      {tags.length > 0 && (
        <section className="px-6 pb-8">
          <div className="max-w-6xl mx-auto flex flex-wrap gap-2 justify-center">
            <Badge
              variant={!selectedTag ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => { setSelectedTag(null); setCurrentPage(1); }}
            >
              All Posts
            </Badge>
            {tags.slice(0, 10).map(tag => (
              <Badge
                key={tag.tag}
                variant={selectedTag === tag.tag ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => { setSelectedTag(tag.tag); setCurrentPage(1); }}
              >
                {tag.tag} ({tag.count})
              </Badge>
            ))}
          </div>
        </section>
      )}

      {/* Posts Grid */}
      <section className="px-6 pb-16">
        <div className="max-w-6xl mx-auto">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p style={{ color: 'hsl(220 10% 50%)' }}>No posts found. Check back soon!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map(post => (
                <Card 
                  key={post.post_id}
                  className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                  onClick={() => navigate(`/blog/${post.slug}`)}
                  data-testid={`blog-post-${post.post_id}`}
                >
                  {post.featured_image && (
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={post.featured_image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    {/* Tags */}
                    {post.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {post.tags.slice(0, 2).map(tag => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    <h2 className="text-xl font-semibold mb-2 group-hover:text-blue-600 transition-colors" 
                        style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(220 15% 25%)' }}>
                      {post.title}
                    </h2>
                    
                    {post.subtitle && (
                      <p className="text-sm mb-2" style={{ color: 'hsl(220 10% 50%)' }}>
                        {post.subtitle}
                      </p>
                    )}
                    
                    <p className="text-sm mb-4 line-clamp-3" style={{ color: 'hsl(220 10% 45%)' }}>
                      {post.excerpt}
                    </p>
                    
                    {/* Meta */}
                    <div className="flex items-center justify-between text-xs" style={{ color: 'hsl(220 10% 55%)' }}>
                      <div className="flex items-center gap-3">
                        {post.author && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {post.author.name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.published_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {post.views_count}
                        </span>
                        <span className="flex items-center gap-1">
                          <Heart className="w-3 h-3" />
                          {post.likes_count}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" />
                          {post.comments_count}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-12">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm" style={{ color: 'hsl(220 10% 50%)' }}>
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 text-center" style={{ backgroundColor: 'hsl(220 15% 55%)' }}>
        <p className="text-white/70 text-sm">
          © {new Date().getFullYear()} Rooted Faith Journal. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default BlogPage;
